import React, {useState} from 'react';
import ConfirmModal from "../../../components/ConfirmModal";
import {Link, LinkAPIData} from "../../../types/Link";
import {useMutation, useQuery} from "react-query";
import ngage from "../../../services/ngage";
import {useNotifications} from "../../../context/NotificationContext";
import {invalidate} from "../../../services/query-client";
import {Option, SingleCombobox} from "../../../components/Form/Form/Control";
import {useOperator} from "../../../context/OperatorContext";
import {LinkFilters} from "../../../services/ngage/links";

// Helpers ----

async function fetchPositionOptions(criteria: LinkFilters): Promise<Array<Option>> {
  const linkCount = await ngage.links.count(criteria)

  return Array(linkCount)
    .fill(null)
    .map((_value, index) => ({
        id: index,
        name: `Position ${index + 1}`
      })
    )
}

// Component ----

type LinkUpdate = {
  id: number,
  data: LinkAPIData
}

type ReorderLinkProps = {
  link: Link | null
  show: boolean
  onReorder?: (link: Link, order: number) => any
  onReorderFail?: (link: Link, order: number, error: unknown) => any
  onCancel?: () => any
}

function ReorderLink({link, show, onReorder, onReorderFail, onCancel}: ReorderLinkProps) {
  const {termFor} = useOperator()
  const {notify} = useNotifications()

  const [previousLink, setPreviousLink] = useState<Link | null>(link ?? null);
  const [order, setOrder] = useState(link?.order ?? 0);

  const linkCriteria = {
    attached: link?.is_attached ?? false,
    linkableType: link?.linkable_type,
    linkableId: link?.linkable_id,
    category: link?.category,
  }

  const {data: options, isLoading: isLoadingOptions} = useQuery(
    ['links.count', linkCriteria],
    () => fetchPositionOptions(linkCriteria)
  )

  const {mutateAsync: updateLink} = useMutation(
    (update: LinkUpdate) => ngage.links.update(update.id, update.data)
  )

  async function handleReorderLink() {
    if (!link) return

    const position = order + 1

    try {
      await updateLink({id: link.id, data: {order}})
      await invalidate('links')

      notify("success", `${termFor('Link')} Moved`, `${termFor('Link')} has been moved to position ${position}`)

      if (onReorder) onReorder(link, order)
    } catch (error: any) {
      notify("error", `${termFor('Link')} Not Moved`, `${termFor('Link')} could not be moved to position ${position}`)

      if (onReorderFail) onReorderFail(link, order, error)
    }
  }

  function handleCancel() {
    if (onCancel) onCancel()
  }

  if(!!link && link.id !== previousLink?.id) {
    setPreviousLink(link)
    setOrder(link.order)
  }

  return (
    <ConfirmModal
      show={show}
      title={`Move this ${termFor('Link')}`}
      body={`Change the order of ${termFor('links')} by moving this one into a new position`}
      approveLabel="Move"
      onApprove={handleReorderLink}
      onCancel={handleCancel}
    >
      <div className="w-full">
        <SingleCombobox
          options={options ?? []}
          selected={options?.find(option => option.id === order)}
          onChange={selected => setOrder(Number(selected?.id))}
          isLoading={isLoadingOptions}
        />
      </div>
    </ConfirmModal>
  );
}

export default ReorderLink;